import type { Move } from '@strategy4kids/protocol';
import { useT } from '../i18n/useT.js';

const MAX_PILLS = 5;

export interface MoveTickerProps {
  readonly side: 'w' | 'b';
  readonly moves: readonly Move[];
  readonly anchor: 'top' | 'bottom';
}

/**
 * One move ticker strip — slides new pills in from the side closest to the
 * player so the eye doesn't have to track across the screen. Caps at
 * {@link MAX_PILLS} visible pills: oldest slides off the trailing edge when
 * the count exceeds the cap.
 *
 * - `anchor="top"`: opponent moves, slides in from the RIGHT.
 * - `anchor="bottom"`: player moves, slides in from the LEFT.
 */
export function MoveTicker({ side, moves, anchor }: MoveTickerProps) {
  const { t } = useT('games');
  const filtered = moves.filter((m) => m.color === side);
  const tail = filtered.slice(-MAX_PILLS);

  const isTop = anchor === 'top';
  return (
    <div
      data-testid={`move-ticker-${anchor}`}
      data-appdom-id={`move-ticker-${anchor}`}
      data-appdom-type="MoveTicker"
      data-ticker-side={side}
      className={`pointer-events-none flex gap-1.5 overflow-hidden ${isTop ? 'justify-end' : 'justify-start'}`}
    >
      {tail.length === 0 ? (
        <span className="rounded-full bg-cream/60 px-2 py-0.5 text-xs font-bold text-ink/40">
          {side === 'w' ? t('your-moves') : t('opponent-moves')}
        </span>
      ) : (
        tail.map((m, i) => (
          <span
            key={`${m.from}-${m.to}-${i}`}
            data-testid={`move-ticker-pill-${anchor}-${i}`}
            className={`animate-[fadeIn_200ms_ease-out] rounded-full px-2 py-0.5 text-xs font-extrabold shadow-sm ${
              side === 'w'
                ? 'bg-cream text-ink ring-1 ring-sun/40 dark:bg-cream dark:text-ink'
                : 'bg-ink text-cream ring-1 ring-ink/30 dark:bg-cream/90 dark:text-ink'
            }`}
          >
            {m.san}
          </span>
        ))
      )}
    </div>
  );
}
